/**
 * ClaimSync Privacy Policy Page
 * Matching homepage design
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  const lastUpdated = 'December 24, 2024';

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);
    
    const header = document.querySelector('header');
    const handleScroll = () => {
      if (header) {
        header.style.boxShadow = window.pageYOffset > 100 ? '0 4px 20px rgba(0, 0, 0, 0.3)' : 'none';
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A1628] border-b border-white/10">
        <nav className="max-w-[1400px] mx-auto px-8 lg:px-12 py-5 flex justify-between items-center">
          <Link to="/home" className="flex items-center">
            <img src="/logo.svg" alt="ClaimSync" className="h-44 lg:h-56 w-auto" />
          </Link>
          <ul className="hidden md:flex items-center gap-8 lg:gap-10">
            <li><Link to="/home#features" className="text-white/85 hover:text-white font-medium text-sm tracking-tight transition-colors">Features</Link></li>
            <li><Link to="/about" className="text-white/85 hover:text-white font-medium text-sm tracking-tight transition-colors">About</Link></li>
            <li><Link to="/contact" className="text-white/85 hover:text-white font-medium text-sm tracking-tight transition-colors">Contact</Link></li>
            <li><Link to="/privacy" className="text-white font-medium text-sm tracking-tight">Privacy</Link></li>
            <li>
              <Link 
                to="/sign-in" 
                className="bg-[#2762EA] hover:bg-[#1e50c7] text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/20"
              >
                Sign In
              </Link>
            </li>
          </ul>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="pt-64 lg:pt-72 pb-12 bg-[#0A1628]">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
            Privacy Policy
          </h1>
          <p className="text-blue-200">Last updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-8 py-16">
        <div className="bg-[#FAFAF9] rounded-2xl p-8 md:p-12 border border-slate-200/50">
          <div className="prose prose-slate max-w-none">
            <Section title="1. Introduction">
              <p>
                ClaimSync ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our business interruption claim calculation service.
              </p>
              <p>
                By using ClaimSync, you agree to the collection and use of information in accordance with this policy.
              </p>
            </Section>

            <Section title="2. Information We Collect">
              <h4 className="font-semibold text-[#0A1628] mt-4 mb-2">Personal Information</h4>
              <p>We may collect the following personal information:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Name and email address</li>
                <li>Account credentials</li>
                <li>Organization/company name</li>
                <li>Professional role and title</li>
              </ul>

              <h4 className="font-semibold text-[#0A1628] mt-4 mb-2">Business Data</h4>
              <p>To provide our services, we process:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Financial records and statements</li>
                <li>Business transaction data</li>
                <li>Claim-related documentation</li>
                <li>Calculation results and reports</li>
              </ul>

              <h4 className="font-semibold text-[#0A1628] mt-4 mb-2">Usage Information</h4>
              <p>We automatically collect:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>IP address and device information</li>
                <li>Browser type and version</li>
                <li>Pages visited and features used</li>
                <li>Time and date of access</li>
              </ul>
            </Section>

            <Section title="3. How We Use Your Information">
              <p>We use collected information to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Provide and maintain our claim calculation services</li>
                <li>Process and generate BI reports</li>
                <li>Authenticate users and manage accounts</li>
                <li>Send service-related communications</li>
                <li>Improve and optimize our platform</li>
                <li>Comply with legal obligations</li>
                <li>Detect and prevent fraud or abuse</li>
              </ul>
            </Section>

            <Section title="4. Data Sharing and Disclosure">
              <p>We may share your information with:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Service Providers:</strong> Third-party vendors who assist in operating our service</li>
                <li><strong>Team Members:</strong> Other users within your organization based on role permissions</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              </ul>
              <p className="mt-4">We do not sell your personal information to third parties.</p>
            </Section>

            <Section title="5. Data Security">
              <p>We implement appropriate technical and organizational measures to protect your data, including:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication through Clerk</li>
                <li>Role-based access controls</li>
                <li>Regular security assessments</li>
                <li>Secure cloud infrastructure</li>
              </ul>
            </Section>

            <Section title="6. Data Retention">
              <p>
                We retain your data for as long as your account is active or as needed to provide services. 
                You may request deletion of your data at any time by contacting us. 
                Some data may be retained longer if required by law or for legitimate business purposes.
              </p>
            </Section>

            <Section title="7. Your Rights">
              <p>Depending on your location, you may have the right to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Delete your data</li>
                <li>Export your data (data portability)</li>
                <li>Opt-out of certain data processing</li>
                <li>Withdraw consent</li>
              </ul>
              <p className="mt-4">To exercise these rights, please contact us at the information provided below.</p>
            </Section>

            <Section title="8. Cookies and Tracking">
              <p>
                We use cookies and similar technologies to maintain sessions, remember preferences, and analyze usage. 
                You can control cookie settings through your browser. Disabling cookies may affect service functionality.
              </p>
            </Section>

            <Section title="9. Third-Party Services">
              <p>
                Our service integrates with third-party providers including Clerk (authentication), 
                cloud hosting providers, and analytics services. These providers have their own privacy policies 
                governing their use of your data.
              </p>
            </Section>

            <Section title="10. Children's Privacy">
              <p>
                ClaimSync is not intended for use by individuals under 18 years of age. 
                We do not knowingly collect personal information from children.
              </p>
            </Section>

            <Section title="11. Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes 
                by posting the new policy on this page and updating the "Last updated" date.
              </p>
            </Section>

            <Section title="12. Contact Us">
              <p>If you have questions about this Privacy Policy or our data practices, please contact us:</p>
              <div className="bg-white rounded-lg p-4 mt-4 border border-slate-200">
                <p className="text-slate-600 text-sm">
                  <strong>Email:</strong> privacy@claimsync.com<br />
                  <strong>Address:</strong> ClaimSync, Inc.<br />
                  <Link to="/contact" className="text-[#2762EA] hover:underline">Contact Form</Link>
                </p>
              </div>
            </Section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0A1628] py-12">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <img src="/logo.svg" alt="ClaimSync" className="h-12 w-auto mb-4" />
              <p className="text-slate-400 text-sm">
                Professional business interruption claim calculations for accountants and legal professionals.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/demo" className="text-slate-400 hover:text-white text-sm transition-colors">Demo</Link></li>
                <li><Link to="/home#features" className="text-slate-400 hover:text-white text-sm transition-colors">Features</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-slate-400 hover:text-white text-sm transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-slate-400 hover:text-white text-sm transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-slate-400 hover:text-white text-sm transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-slate-400 hover:text-white text-sm transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} ClaimSync. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-bold text-[#0A1628] mb-4">{title}</h2>
      <div className="text-slate-600 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}
